import { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  BulkTripPriceChangeItemRead,
  useTripsBulkPriceConfirmCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';
import { tripSlice } from 'src/features/trip/trip.slice';

export function useConfirmPriceStepViewModel(
  setOpen: (open: boolean) => void,
  setError: (error: string | null) => void
) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const bulkPriceResults = useAppSelector(
    (state) => state.trip.bulkPriceModal.results
  );
  const [confirmNewPrices] = useTripsBulkPriceConfirmCreateMutation();

  const selectedBulkPriceTripRows = useAppSelector(
    (state) => state.trip.selectedBulkPriceTripRows
  );

  const selectedBulkPriceTripRowsAsBoolean = useMemo(() => {
    const result: Record<string, boolean> = {};
    Object.keys(selectedBulkPriceTripRows).forEach((id) => {
      result[id] = !!selectedBulkPriceTripRows[id];
    });
    return result;
  }, [selectedBulkPriceTripRows]);

  const setRowSelection = useCallback(
    (setter: (update: Record<string, boolean>) => Record<string, boolean>) => {
      const next: Record<string, BulkTripPriceChangeItemRead> = {};

      const result = setter(selectedBulkPriceTripRowsAsBoolean);
      Object.keys(result).forEach((id) => {
        const value = result[id];
        const trip =
          bulkPriceResults.trips_repriced.find((t) => t.id === id) ?? null;

        if (value && trip) {
          next[id] = trip as BulkTripPriceChangeItemRead;
        }
      });

      dispatch(
        tripSlice.actions.setSelectedBulkPriceTripRows(
          next as Record<string, BulkTripPriceChangeItemRead>
        )
      );
    },
    [
      dispatch,
      selectedBulkPriceTripRowsAsBoolean,
      bulkPriceResults.trips_repriced,
    ]
  );

  const getRowId = useCallback(
    (row: BulkTripPriceChangeItemRead) => row.id,
    []
  );

  const tableClass =
    bulkPriceResults.trips_outside_date_range.length > 0 &&
    bulkPriceResults.trips_repriced.length > 0
      ? 'max-h-[25vh]'
      : 'max-h-[50vh]';

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);

    const selectedTripsWithNewPrices = Object.keys(
      selectedBulkPriceTripRows
    ).map((id) => {
      return selectedBulkPriceTripRows[id];
    });

    try {
      const tripsUpdatedResponse = await confirmNewPrices({
        bulkTripPriceChangeConfirm: {
          confirmed_trips: selectedTripsWithNewPrices
            .filter(
              (
                trip
              ): trip is BulkTripPriceChangeItemRead & {
                new_price_cents: number;
              } => trip != null && trip.new_price_cents != null
            )
            .map((trip) => ({
              id: trip.id,
              new_price_cents: trip.new_price_cents,
            })),
        },
      }).unwrap();

      if (tripsUpdatedResponse.trips_updated > 0) {
        dispatch(
          show({
            title: 'Pricing Updated',
            description: `Successfully updated pricing for ${tripsUpdatedResponse.trips_updated} trip(s).`,
            type: 'success',
          })
        );
      }

      setOpen(false);
    } catch (error) {
      setError(formatServerError(error));
    } finally {
      setLoading(false);
    }
  }, [
    setError,
    selectedBulkPriceTripRows,
    confirmNewPrices,
    setOpen,
    dispatch,
  ]);

  return {
    bulkPriceResults,
    getRowId,
    handleSubmit,
    loading,
    selectedBulkPriceTripRowsAsBoolean,
    setRowSelection,
    tableClass,
  };
}
